import React from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { useContactForm } from "@/hooks/useContactForm";
import FormSuccessMessage from "@/components/FormSuccessMessage";
import {
  NameAndPhone, EmailField, AddressField, ServiceAndTimeline,
  MessageField, PreferredContactPicker, HoneypotField,
} from "@/components/form/ContactFormFields";

const DEFAULT_SUCCESS =
  "Thank you for reaching out to Black Ridge Construction. We received your request and will follow up as soon as possible.";

export default function ContactForm({
  variant = "default",
  endpoint = "/leads",
  successMessage = DEFAULT_SUCCESS,
  showAddress = true,
  showTimeline = true,
  showContactMethod = true,
  defaultService = "",
  title,
  source_page,
  dark = false,
}) {
  const initial = React.useMemo(() => ({ service: defaultService }), [defaultService]);
  const { data, update, state, submit, reset } = useContactForm({ endpoint, initial, source_page });

  if (state.success) {
    return <FormSuccessMessage message={successMessage} dark={dark} onReset={reset} />;
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
      {title && (
        <div className="mb-1">
          <h3 className="font-display text-xl tracking-wider uppercase">{title}</h3>
          <div className="divider-bronze mt-2" />
        </div>
      )}

      <NameAndPhone data={data} update={update} />
      <EmailField data={data} update={update} />
      {showAddress && <AddressField data={data} update={update} />}
      <ServiceAndTimeline data={data} update={update} showTimeline={showTimeline} />
      <MessageField data={data} update={update} rows={variant === "hero" ? 3 : 4} />
      {showContactMethod && <PreferredContactPicker data={data} update={update} />}
      <HoneypotField data={data} update={update} />

      {state.error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded" data-testid="form-error">
          <AlertCircle size={16} /> {state.error}
        </div>
      )}

      <button type="submit" disabled={state.loading} className="br-btn-primary w-full justify-center" data-testid="form-submit">
        {state.loading ? <Loader2 className="animate-spin" size={16} /> : null}
        {state.loading ? "Sending..." : "Request a Consultation"}
      </button>
      <p className="text-[11px] text-gray-500 leading-relaxed">
        By submitting this form, you agree to be contacted by Black Ridge Construction about your project or service request.
      </p>
    </form>
  );
}
